import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useAxiosInstance } from "../../../services/Interceptor";
import { getMeasurements } from "../../../services/AthleteService";
import { getMeasurementsResultsByEvent } from "../../../services/CombineService";
import ProgressBar from "../athlete-progress-bar";
import LoadingSpinner from "../../LoadingSpinner";
import AthleteMeasurementsListAthletic from "./athlete-measurements-list-athletic";
import AthleteMeasurementsListPosition from "./athlete-measurements-list-position";
import AthleteMeasurementVideoViewer from "./athlete_measurement_videoviewer";
import VideoPlayer from "../VideoPlayer";
import PremiumFeatureModal from "../../PremiumFeatureModal";
import FreeTrialModal from "../../FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import { usePremiumUpsellRedirect } from "../../../hooks/usePremiumUpsellRedirect";
import { setProfileAction } from "../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";
import { useSubscriptionAccess } from "../../../hooks/useSubscriptionAccess";

export default function AthleteMeasurements({ profile = {} }) {
  const apiInstance = useAxiosInstance();
  const [loading, setLoading] = useState(true);
  const [listInfo, setListInfo] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);

  const r_athlete = useSelector((state) => state.athletes);
  const {
    buttons: {
      measurements: {
        tab,
        athleteTabSelected,
        positionTabSelected,
        positionYearTabSelected,
      },
    },
  } = r_athlete;

  const dispatch = useDispatch();
  const { user, hasSubscriptionAccess, showFreeTrial } =
    useSubscriptionAccess();

  const closeModal = () => setIsOpen(false);

  const openSubscription = usePremiumUpsellRedirect(user);

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const getData = async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    if (r_athlete.config.searchEvent && !r_athlete.config.eventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let results = [];
      if (r_athlete.config.searchEvent && r_athlete.config.eventId) {
        results = await getMeasurementsResultsByEvent(
          apiInstance,
          profile?.id,
          r_athlete.config.eventId,
        );
      } else {
        results = await getMeasurements(apiInstance, profile?.id);
      }
      if (results.status === 200) {
        setListInfo(results.data);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!profile?.id) return;
    if (r_athlete.config.searchEvent && !r_athlete.config.eventId) return;

    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, r_athlete.config.eventId]);

  if (r_athlete.config.searchEvent && !r_athlete.config.eventId)
    return <div className="text-center">Please select an event</div>;

  if (loading)
    return (
      <div className="my-10">
        <LoadingSpinner />
      </div>
    );

  return (
    listInfo &&
    !r_athlete.loading && (
      <>
        <PremiumFeatureModal
          open={openPremiumFeatureModal}
          onClose={() => setOpenPremiumFeatureModal(false)}
          title="Premium Feature"
          description="This feature is locked. Start a free trial or subscribe now to access it."
          onStartFreeTrial={handlePremiumFreeTrial}
          onSubscribe={handlePremiumSubscribe}
          showStartFreeTrial={showFreeTrial}
        />
        <FreeTrialModal
          open={openTrialModal}
          onClose={() => setOpenTrialModal(false)}
          user={user}
          onStoreSuccessChain={async () => {
            const result = await StartFreeTrial(apiInstance, user.id);
            if (result.success) {
              toast.success("Free trial started!", { position: "top-right" });
              setOpenTrialModal(false);
              await dispatch(setProfileAction(apiInstance, user.id));
              await dispatch(
                validateSubscriptionAction(apiInstance, user.role, user.id),
              );
              dispatch(bumpMenuRefresh());
            } else {
              toast.error("Failed to start free trial", {
                position: "top-right",
              });
            }
          }}
        />
        <div className="mt-1 px-4 -m-4 overflow-y-auto relative">
          {tab && athleteTabSelected && (
            <div className="mt-2">
              <div className="mb-2 bg-blue-50 rounded-lg border border-blue-200 px-3 py-2 items-center">
                <div className="font-medium ">
                  Trackit Measurement Score
                  <div className="w-full ">
                    <ProgressBar
                      score={
                        listInfo.measurementScores?.measurementScoreData
                          ? Math.round(
                              listInfo.measurementScores.measurementScoreData,
                            )
                          : 0
                      }
                    />
                  </div>
                  <div className="w-full ">
                    <div className="text-blue-600 text-sm">
                      <AthleteMeasurementVideoViewer
                        title="Overhead Squat"
                        videoUrls={
                          listInfo?.measurementVideosByType?.Full?.map(
                            (v) => v.filePath,
                          ) || []
                        }
                        hasSubscriptionAccess={hasSubscriptionAccess}
                        onBlock={() => setOpenPremiumFeatureModal(true)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <AthleteMeasurementsListAthletic result={listInfo} />
            </div>
          )}
          {tab && positionTabSelected && (
            <div className="mt-2">
              <div className="mb-2 bg-blue-50 rounded-lg border border-blue-200 px-3 py-2 items-center">
                <div className="font-medium ">
                  Position Measurement Score
                  <div className="w-full ">
                    <div className="w-full ">
                      <ProgressBar
                        score={
                          listInfo.measurementScores?.positionMeasurementScore
                            ? Math.round(
                                listInfo.measurementScores
                                  .positionMeasurementScore,
                              )
                            : 0
                        }
                      />
                    </div>
                  </div>
                  <div className="w-full ">
                    <div className="text-blue-600 text-sm">
                      <AthleteMeasurementVideoViewer
                        title="Overhead Squat"
                        videoUrls={
                          listInfo?.measurementVideosByType?.Full?.map(
                            (v) => v.filePath,
                          ) || []
                        }
                        hasSubscriptionAccess={hasSubscriptionAccess}
                        onBlock={() => setOpenPremiumFeatureModal(true)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <AthleteMeasurementsListPosition result={listInfo} />
            </div>
          )}
          {tab && positionYearTabSelected && (
            <div className="mt-2">
              <div className="mb-2 bg-blue-50 rounded-lg border border-blue-200 px-3 py-2 items-center">
                <div className="font-medium ">
                  Position/Age Measurement score
                  <div className="w-full ">
                    <div className="w-full ">
                      <ProgressBar
                        score={
                          listInfo.measurementScores
                            ?.positionYearMeasurementScore
                            ? Math.round(
                                listInfo.measurementScores
                                  .positionYearMeasurementScore,
                              )
                            : listInfo.measurementScores
                                  ?.positionMeasurementScore
                              ? Math.round(
                                  listInfo.measurementScores
                                    .positionMeasurementScore,
                                )
                              : 0
                        }
                      />
                    </div>
                  </div>
                  <div className="w-full ">
                    <div className="text-blue-600 text-sm">
                      <AthleteMeasurementVideoViewer
                        title="Overhead Squat"
                        videoUrls={
                          listInfo?.measurementVideosByType?.Full?.map(
                            (v) => v.filePath,
                          ) || []
                        }
                        hasSubscriptionAccess={hasSubscriptionAccess}
                        onBlock={() => setOpenPremiumFeatureModal(true)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <AthleteMeasurementsListPosition result={listInfo} />
            </div>
          )}
        </div>

        {/* Video Modal Pop up */}
        {isOpen && (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="absolute inset-0 bg-gray-900 opacity-50"></div>
            <div className="bg-white rounded-lg shadow-lg z-50 max-w-screen-lg w-full p-4 m-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Measurement Score</h2>
                <button
                  className="text-gray-600 hover:text-black text-xl font-bold safari-text"
                  onClick={closeModal}
                >
                  ✕
                </button>
              </div>
              <div className="relative p-0 w-full">
                <VideoPlayer
                  src="https://ik.imagekit.io/ikmedia/example_video.mp4"
                  width="100%"
                  height="300px"
                  overlay={false}
                />
              </div>
            </div>
          </div>
        )}
      </>
    )
  );
}
